import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Activity,
  Play,
  Pause,
  AlertTriangle,
  FlaskConical,
  CheckCircle2,
  XCircle,
  Download,
  Mail,
  TrendingUp,
  Lock,
  Unlock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_LABELS = {
  off: { label: 'OFF (logging disabled)', color: 'bg-slate-500' },
  log_only: { label: 'LOG ONLY (validation)', color: 'bg-blue-500' },
  enforce: { label: 'ENFORCING (403 on deny)', color: 'bg-red-600' },
};

// Known modules — used to flag missing coverage
const EXPECTED_MODULES = [
  'tasks',
  'dev_queue',
  'uiux_queue',
  'social_media',
  'reports',
  'settings',
];

const LOW_COVERAGE_THRESHOLD = 5; // <5 checks = low coverage
const MISCONFIG_DENY_PCT = 20; // >20% denies for a role = potential misconfig
const LIVE_TAIL_POLL_MS = 5000;
const LIVE_TAIL_LIMIT = 50;

const RBACDebug = () => {
  const { token, user } = useAuth();
  const { isManager } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deniedOnly, setDeniedOnly] = useState('all');
  const [permissionFilter, setPermissionFilter] = useState('all');
  const [mode, setMode] = useState(null);

  // Live tail state
  const [tail, setTail] = useState([]);
  const [tailPaused, setTailPaused] = useState(false);
  const [tailLastFetch, setTailLastFetch] = useState(null);
  const tailPausedRef = useRef(false);
  useEffect(() => {
    tailPausedRef.current = tailPaused;
  }, [tailPaused]);

  // Simulation state
  const [simLastN, setSimLastN] = useState('100');
  const [simRole, setSimRole] = useState('all');
  const [simModule, setSimModule] = useState('all');
  const [simAction, setSimAction] = useState('all');
  const [simDeniedOnly, setSimDeniedOnly] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Email state
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');

  // Trend state
  const [trend, setTrend] = useState(null);

  // Enforcement wizard state
  const [readiness, setReadiness] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardConfirm, setWizardConfirm] = useState(false);
  const [wizardText, setWizardText] = useState('');
  const [wizardReason, setWizardReason] = useState('');
  const [wizardSubmitting, setWizardSubmitting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, modeRes] = await Promise.all([
        axios.get(`${API}/rbac/permission-logs/summary`, { headers: authHeaders }),
        axios.get(`${API}/rbac/enforcement-mode`, { headers: authHeaders }),
      ]);
      setSummary(sumRes.data);
      setMode(modeRes.data);

      const params = new URLSearchParams({ limit: '50' });
      if (deniedOnly === 'denied') params.append('granted_by_rbac', 'false');
      if (deniedOnly === 'granted') params.append('granted_by_rbac', 'true');
      if (permissionFilter !== 'all') params.append('permission_name', permissionFilter);
      const logsRes = await axios.get(`${API}/rbac/permission-logs?${params.toString()}`, {
        headers: authHeaders,
      });
      setLogs(logsRes.data.logs || []);
    } catch (e) {
      console.error('RBAC debug load failed', e);
    } finally {
      setLoading(false);
    }
  }, [deniedOnly, permissionFilter, token]);

  useEffect(() => {
    load();
  }, [load]);

  // ============ Simulation (read-only enforcement dry-run) ============
  const runSimulation = useCallback(async () => {
    try {
      setSimLoading(true);
      const params = new URLSearchParams({ last_n: String(simLastN || 100) });
      if (simRole !== 'all') params.append('role_level', simRole);
      if (simModule !== 'all') params.append('module', simModule);
      if (simAction !== 'all') params.append('action', simAction);
      if (simDeniedOnly) params.append('denied_only', 'true');
      const res = await axios.get(
        `${API}/rbac/simulate-enforcement?${params.toString()}`,
        { headers: authHeaders }
      );
      setSimResult(res.data);
    } catch (e) {
      console.error('Simulation failed', e);
    } finally {
      setSimLoading(false);
    }
    // eslint-disable-next-line
  }, [simLastN, simRole, simModule, simAction, simDeniedOnly, token]);

  const exportSimulation = useCallback(async () => {
    try {
      const params = new URLSearchParams({ last_n: String(simLastN || 100) });
      if (simRole !== 'all') params.append('role_level', simRole);
      if (simModule !== 'all') params.append('module', simModule);
      if (simAction !== 'all') params.append('action', simAction);
      if (simDeniedOnly) params.append('denied_only', 'true');
      const res = await axios.get(
        `${API}/rbac/simulate-enforcement/export?${params.toString()}`,
        { headers: authHeaders, responseType: 'blob' }
      );
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `rbac_simulation_${Date.now()}.csv`;
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
    // eslint-disable-next-line
  }, [simLastN, simRole, simModule, simAction, simDeniedOnly, token]);

  const loadEmailStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/rbac/simulate-enforcement/email-status`,
        { headers: authHeaders }
      );
      setEmailStatus(res.data);
      if (!emailRecipient && res.data?.configured_recipient) {
        setEmailRecipient(res.data.configured_recipient);
      }
    } catch (e) {
      console.error('Email status load failed', e);
    }
    // eslint-disable-next-line
  }, [token, emailRecipient]);

  useEffect(() => {
    if (isManager) loadEmailStatus();
  }, [loadEmailStatus, user?.role_level]);

  const loadTrend = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/rbac/deny-rate-trend?days=14`,
        { headers: authHeaders }
      );
      setTrend(res.data);
    } catch (e) {
      console.error('Trend load failed', e);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (isManager) loadTrend();
  }, [loadTrend, isManager]);

  const loadReadiness = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/rbac/enforcement-readiness`,
        { headers: authHeaders }
      );
      setReadiness(res.data);
    } catch (e) {
      console.error('Readiness load failed', e);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (isManager) loadReadiness();
  }, [loadReadiness, user?.role_level]);

  const flipEnforcementMode = useCallback(
    async (targetMode) => {
      try {
        setWizardSubmitting(true);
        const body = { mode: targetMode, reason: wizardReason || undefined };
        if (targetMode === 'enforce') {
          body.confirmation_text = wizardText.trim();
        }
        const res = await axios.post(
          `${API}/rbac/enforcement-mode`,
          body,
          { headers: authHeaders }
        );
        toast.success(res.data.message);
        setWizardOpen(false);
        setWizardConfirm(false);
        setWizardText('');
        setWizardReason('');
        loadReadiness();
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Mode change failed');
      } finally {
        setWizardSubmitting(false);
      }
      // eslint-disable-next-line
    },
    [wizardText, wizardReason, token]
  );

  const emailSimulation = useCallback(async () => {
    if (!emailRecipient && !emailStatus?.configured_recipient) {
      toast.error('Set a recipient email or configure RBAC_REPORT_EMAIL in backend/.env.');
      return;
    }
    try {
      setEmailSending(true);
      const body = {
        last_n: Number(simLastN || 100),
        denied_only: simDeniedOnly,
      };
      if (emailRecipient) body.to = emailRecipient;
      if (simRole !== 'all') body.role_level = simRole;
      if (simModule !== 'all') body.module = simModule;
      if (simAction !== 'all') body.action = simAction;
      const res = await axios.post(
        `${API}/rbac/simulate-enforcement/email`,
        body,
        { headers: authHeaders }
      );
      toast.success(
        `Report emailed to ${res.data.recipient} (${res.data.sent_today}/${res.data.daily_limit} used today).`
      );
      loadEmailStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Email send failed.';
      if (e?.response?.status === 429) {
        toast.error(`Rate limit: ${detail}`);
      } else {
        toast.error(detail);
      }
    } finally {
      setEmailSending(false);
    }
    // eslint-disable-next-line
  }, [simLastN, simRole, simModule, simAction, simDeniedOnly, emailRecipient, emailStatus, token]);

  // ============ Live tail polling ============
  useEffect(() => {
    if (!isManager) return undefined;
    let cancelled = false;

    const fetchTail = async () => {
      if (tailPausedRef.current) return;
      try {
        const res = await axios.get(
          `${API}/rbac/permission-logs?limit=${LIVE_TAIL_LIMIT}`,
          { headers: authHeaders }
        );
        if (!cancelled) {
          setTail(res.data.logs || []);
          setTailLastFetch(new Date());
        }
      } catch (e) {
        // Silently continue — validation-phase tail should be resilient
      }
    };

    fetchTail();
    const id = setInterval(fetchTail, LIVE_TAIL_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line
  }, [token, isManager]);

  if (!isManager) {
    return (
      <div className="p-8" data-testid="rbac-debug-forbidden">
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <p className="text-lg font-medium">Admin access only</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const modeInfo = mode ? MODE_LABELS[mode.mode] || MODE_LABELS.off : MODE_LABELS.off;
  const allPermissions = summary?.by_permission?.map((p) => p.permission) || [];

  // ============ Computed validation indicators ============
  const misconfigRoles = (summary?.by_role_level || []).filter((r) => {
    if (!r.total) return false;
    const pct = (r.denied / r.total) * 100;
    return pct > MISCONFIG_DENY_PCT;
  });

  const coveredModules = new Set((summary?.by_module || []).map((m) => m.module));
  const missingModules = EXPECTED_MODULES.filter((m) => !coveredModules.has(m));
  const lowCoverageModules = (summary?.by_module || []).filter(
    (m) => m.total > 0 && m.total < LOW_COVERAGE_THRESHOLD
  );

  return (
    <div className="p-6 space-y-6" data-testid="rbac-debug-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-blue-600" />
            RBAC Debug Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Phase 3 validation — review permission checks before enforcement.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Enforcement Mode
            </div>
            <Badge
              className={`${modeInfo.color} text-white text-xs`}
              data-testid="rbac-mode-badge"
            >
              {modeInfo.label}
            </Badge>
          </div>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            data-testid="rbac-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Enforcement Wizard */}
      {readiness && (
        <Card
          className={
            readiness.current_mode === 'enforce'
              ? 'border-2 border-red-500 bg-red-50'
              : 'border-2 border-indigo-200'
          }
          data-testid="card-wizard"
        >
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {readiness.current_mode === 'enforce' ? (
                  <Lock className="h-4 w-4 text-red-600" />
                ) : (
                  <Unlock className="h-4 w-4 text-indigo-600" />
                )}
                Enforcement Wizard
              </CardTitle>
              <div className="text-xs text-slate-500 mt-1">
                Current mode:{' '}
                <code
                  className={`px-1.5 py-0.5 rounded ${
                    readiness.current_mode === 'enforce'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-100 text-slate-800'
                  }`}
                  data-testid="wizard-current-mode"
                >
                  {readiness.current_mode}
                </code>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {readiness.current_mode !== 'enforce' ? (
                <Button
                  onClick={() => setWizardOpen(true)}
                  disabled={!readiness.ready}
                  size="sm"
                  variant={readiness.ready ? 'default' : 'outline'}
                  data-testid="wizard-enable-btn"
                  title={
                    readiness.ready
                      ? 'Open the Enable Enforcement wizard'
                      : 'Preconditions not met — see checklist below'
                  }
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {readiness.ready ? 'Enable Enforcement' : 'Enable Enforcement (blocked)'}
                </Button>
              ) : (
                <Button
                  onClick={() => flipEnforcementMode('log_only')}
                  disabled={wizardSubmitting}
                  size="sm"
                  variant="destructive"
                  data-testid="wizard-rollback-btn"
                >
                  {wizardSubmitting ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Unlock className="h-4 w-4 mr-2" />
                  )}
                  Rollback → log_only
                </Button>
              )}
              <Button
                onClick={loadReadiness}
                variant="outline"
                size="sm"
                data-testid="wizard-refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <ul className="space-y-1.5 text-sm" data-testid="wizard-checklist">
              {readiness.checks.map((c) => (
                <li
                  key={c.id}
                  className="flex items-start gap-2"
                  data-testid={`wizard-check-${c.id}`}
                >
                  {c.passed ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <div className={c.passed ? 'text-slate-700' : 'text-red-700 font-medium'}>
                      {c.label}
                    </div>
                    <div className="text-xs text-slate-500">{c.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
            {!readiness.ready && readiness.current_mode !== 'enforce' && (
              <div className="text-xs text-slate-500 border-t pt-2">
                Button will unlock automatically when every precondition passes. The wizard
                <strong> cannot</strong> be bypassed.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Enforcement Wizard — Confirmation Modal */}
      {wizardOpen && readiness && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          data-testid="wizard-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Lock className="h-6 w-6 text-red-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold">Enable RBAC Enforcement</h3>
                <p className="text-sm text-slate-600">
                  This will block any request where the new RBAC role lacks the required
                  permission. Reversible at any time via the rollback button.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Stability</span>
                <span className="font-medium">{readiness.summary?.stability_label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Deny rate (sim)</span>
                <span className="font-medium">{readiness.summary?.deny_rate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Modules covered</span>
                <span className="font-medium">
                  {readiness.summary?.modules_covered}/{readiness.summary?.expected_modules}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Roles observed</span>
                <span className="font-medium">
                  {readiness.summary?.roles_observed?.join(', ') || '—'}
                </span>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={wizardConfirm}
                onChange={(e) => setWizardConfirm(e.target.checked)}
                data-testid="wizard-confirm-checkbox"
                className="mt-1"
              />
              <span>I confirm all validation conditions are met.</span>
            </label>

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Type <code>{readiness.required_confirmation_text}</code> to proceed
              </label>
              <input
                type="text"
                value={wizardText}
                onChange={(e) => setWizardText(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                placeholder={readiness.required_confirmation_text}
                data-testid="wizard-confirm-text"
                autoFocus
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">
                Reason (optional, audit trail)
              </label>
              <input
                type="text"
                value={wizardReason}
                onChange={(e) => setWizardReason(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                placeholder="e.g. Post-validation rollout, ticket RBAC-123"
                data-testid="wizard-reason"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setWizardOpen(false);
                  setWizardConfirm(false);
                  setWizardText('');
                  setWizardReason('');
                }}
                data-testid="wizard-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => flipEnforcementMode('enforce')}
                disabled={
                  !wizardConfirm ||
                  wizardText.trim() !== readiness.required_confirmation_text ||
                  wizardSubmitting
                }
                data-testid="wizard-submit-btn"
              >
                {wizardSubmitting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4 mr-2" />
                )}
                Enable Enforcement Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Validation alerts */}
      {(misconfigRoles.length > 0 || missingModules.length > 0 || lowCoverageModules.length > 0) && (
        <div className="space-y-2" data-testid="rbac-alerts">
          {misconfigRoles.length > 0 && (
            <div
              className="flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 p-3 rounded"
              data-testid="alert-misconfig"
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">
                  Potential misconfiguration detected
                </div>
                <div className="text-amber-800">
                  These roles have a deny rate above {MISCONFIG_DENY_PCT}% — check mapping before
                  enforcing:{' '}
                  {misconfigRoles
                    .map(
                      (r) =>
                        `${r.role_level || 'unknown'} (${(
                          (r.denied / r.total) *
                          100
                        ).toFixed(1)}%)`
                    )
                    .join(', ')}
                  .
                </div>
              </div>
            </div>
          )}
          {missingModules.length > 0 && (
            <div
              className="flex items-start gap-3 border-l-4 border-slate-500 bg-slate-50 p-3 rounded"
              data-testid="alert-missing-coverage"
            >
              <AlertTriangle className="h-5 w-5 text-slate-500 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-slate-800">Missing permission coverage</div>
                <div className="text-slate-600">
                  No logs observed yet for module(s):{' '}
                  <code>{missingModules.join(', ')}</code>. Trigger real usage or verify that
                  endpoint logging hooks are wired.
                </div>
              </div>
            </div>
          )}
          {lowCoverageModules.length > 0 && (
            <div
              className="flex items-start gap-3 border-l-4 border-blue-400 bg-blue-50 p-3 rounded"
              data-testid="alert-low-coverage"
            >
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-blue-900">Low coverage modules</div>
                <div className="text-blue-800">
                  {lowCoverageModules
                    .map((m) => `${m.module} (${m.total} checks)`)
                    .join(', ')}{' '}
                  — &lt; {LOW_COVERAGE_THRESHOLD} checks. Let traffic accumulate before drawing
                  conclusions.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total-checks">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
              <Activity className="h-3.5 w-3.5" /> Total Checks
            </div>
            <div className="text-3xl font-bold mt-2">{summary?.total_checks ?? '—'}</div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-deny-rate">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
              <ShieldX className="h-3.5 w-3.5" /> RBAC Deny Rate
            </div>
            <div className="text-3xl font-bold mt-2">
              {summary ? `${summary.deny_rate}%` : '—'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {summary?.total_denied_by_rbac ?? 0} denied checks
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-modules-covered">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
              Modules Covered
            </div>
            <div className="text-3xl font-bold mt-2">
              {coveredModules.size}
              <span className="text-base text-slate-500 font-normal">
                {' / '}
                {EXPECTED_MODULES.length}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {Array.from(coveredModules).join(', ') || '—'}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-roles-seen">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
              Roles Observed
            </div>
            <div className="text-3xl font-bold mt-2">{summary?.by_role_level?.length ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">
              {summary?.by_role_level?.map((r) => r.role_level).join(', ') || '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 14-day deny-rate trend */}
      {trend && (
        <Card data-testid="card-trend">
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-600" />
                14-Day Deny-Rate Trend
              </CardTitle>
              <div className="text-xs text-slate-500 mt-1">
                Daily aggregate from <code>rbac_permission_logs</code>. Last 2 days are
                highlighted. Stability criterion:{' '}
                <strong>both of the last 2 days have activity and zero denies</strong>.
              </div>
            </div>
            {(() => {
              const map = {
                stable: { bg: 'bg-green-600', label: 'Stable for Enforcement', Icon: CheckCircle2 },
                unstable: { bg: 'bg-red-600', label: 'Unstable', Icon: XCircle },
                insufficient: { bg: 'bg-amber-500', label: 'Unstable – Insufficient Data', Icon: AlertTriangle },
              };
              const info = map[trend.stability_status] || map.insufficient;
              const { Icon } = info;
              return (
                <Badge
                  className={`${info.bg} text-white text-xs px-3 py-1.5`}
                  data-testid="trend-stability-badge"
                >
                  <Icon className="h-3.5 w-3.5 mr-1 inline" />
                  {trend.stability_label || info.label}
                </Badge>
              );
            })()}
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-56 w-full" data-testid="trend-chart">
              <ResponsiveContainer>
                <BarChart
                  data={trend.series.map((d) => ({
                    ...d,
                    shortDate: d.date.slice(5), // MM-DD
                  }))}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="shortDate"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, (dataMax) => Math.max(10, Math.ceil(dataMax))]}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value, name, ctx) => {
                      if (name === 'deny_rate') return [`${value}%`, 'Deny rate'];
                      return [value, name];
                    }}
                    labelFormatter={(label, payload) => {
                      const row = payload?.[0]?.payload;
                      return row ? `${row.date} · ${row.total} checks · ${row.denied} denied` : label;
                    }}
                  />
                  <ReferenceLine y={5} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Warn 5%', fontSize: 10, fill: '#f59e0b', position: 'insideTopRight' }} />
                  <ReferenceLine y={10} stroke="#dc2626" strokeDasharray="4 4" label={{ value: 'Critical 10%', fontSize: 10, fill: '#dc2626', position: 'insideTopRight' }} />
                  <Bar dataKey="deny_rate" radius={[3, 3, 0, 0]}>
                    {trend.series.map((d, i) => {
                      const isLastTwo = i >= trend.series.length - 2;
                      const zeroDenies = d.denied === 0 && d.total > 0;
                      const color =
                        d.deny_rate >= 10 ? '#dc2626'
                          : d.deny_rate > 5 ? '#f59e0b'
                            : d.total === 0 ? '#cbd5e1'
                              : isLastTwo && zeroDenies ? '#10b981'
                                : isLastTwo ? '#f59e0b'
                                  : '#6366f1';
                      return <Cell key={d.date} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {trend.series.slice(-2).map((d, i) => (
                <div
                  key={d.date}
                  className={`text-xs p-2 rounded border-l-4 ${
                    d.denied === 0 && d.total > 0
                      ? 'border-green-500 bg-green-50'
                      : d.total === 0
                      ? 'border-slate-300 bg-slate-50'
                      : 'border-red-500 bg-red-50'
                  }`}
                  data-testid={`trend-recent-day-${i}`}
                >
                  <div className="font-medium">
                    {d.date} {i === 1 ? '(today)' : '(yesterday)'}
                  </div>
                  <div className="text-slate-600">
                    {d.total} checks · {d.denied} denied · deny rate {d.deny_rate}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="card-by-module">
          <CardHeader>
            <CardTitle className="text-base">By Module</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead className="text-right">Checks</TableHead>
                  <TableHead className="text-right">Denied</TableHead>
                  <TableHead className="text-right">Deny %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary?.by_module || []).map((m) => (
                  <TableRow key={m.module} data-testid={`row-module-${m.module}`}>
                    <TableCell className="font-medium">{m.module}</TableCell>
                    <TableCell className="text-right">{m.total}</TableCell>
                    <TableCell className="text-right">
                      {m.denied > 0 ? (
                        <span className="text-red-600 font-medium">{m.denied}</span>
                      ) : (
                        m.denied
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-slate-500">
                      {m.total ? `${((m.denied / m.total) * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card data-testid="card-by-role">
          <CardHeader>
            <CardTitle className="text-base">By Role — Allow / Deny ratio</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {(summary?.by_role_level || []).map((r) => {
              const total = r.total || 0;
              const denied = r.denied || 0;
              const allowed = total - denied;
              const denyPct = total ? (denied / total) * 100 : 0;
              const allowPct = 100 - denyPct;
              return (
                <div
                  key={r.role_level || 'unknown'}
                  data-testid={`row-role-${r.role_level}`}
                  className="space-y-1"
                >
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">{r.role_level || '(unknown)'}</span>
                    <span className="text-slate-500">
                      <span className="text-green-700">{allowed} allowed</span>
                      {' · '}
                      <span className={denied ? 'text-red-600' : 'text-slate-500'}>
                        {denied} denied
                      </span>
                      {' · '}
                      {total} total
                    </span>
                  </div>
                  <div className="flex h-2 w-full overflow-hidden rounded bg-slate-100">
                    <div
                      className="bg-green-500"
                      style={{ width: `${allowPct}%` }}
                      title={`Allow ${allowPct.toFixed(1)}%`}
                    />
                    <div
                      className="bg-red-500"
                      style={{ width: `${denyPct}%` }}
                      title={`Deny ${denyPct.toFixed(1)}%`}
                    />
                  </div>
                </div>
              );
            })}
            {!summary?.by_role_level?.length && (
              <div className="text-sm text-slate-500 text-center py-6">No role data yet.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Permission usage + top denied */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="card-by-permission">
          <CardHeader>
            <CardTitle className="text-base">Permission Usage</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Permission</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Denied</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary?.by_permission || []).map((p) => (
                  <TableRow key={p.permission}>
                    <TableCell className="font-mono text-xs">{p.permission}</TableCell>
                    <TableCell className="text-right">{p.total}</TableCell>
                    <TableCell className="text-right">
                      {p.denied > 0 ? (
                        <span className="text-red-600 font-medium">{p.denied}</span>
                      ) : (
                        p.denied
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card data-testid="card-top-denied">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldX className="h-4 w-4 text-red-500" /> Top Denied (permission × role)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 max-h-96 overflow-auto">
            {summary?.top_denied?.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Permission</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.top_denied.map((t, i) => (
                    <TableRow key={`${t.permission}-${t.role_level}-${i}`}>
                      <TableCell className="font-mono text-xs">{t.permission}</TableCell>
                      <TableCell>{t.role_level || '(unknown)'}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium">
                        {t.count}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-sm text-slate-500 py-10">
                <ShieldCheck className="mx-auto h-8 w-8 text-green-500 mb-2" />
                No denies recorded — RBAC fully aligned with legacy checks so far.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Live Tail */}
      <Card data-testid="card-live-tail">
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Live Tail
              <Badge
                className={
                  tailPaused ? 'bg-slate-400 text-white' : 'bg-green-500 text-white animate-pulse'
                }
                data-testid="tail-status-badge"
              >
                {tailPaused ? 'PAUSED' : 'LIVE'}
              </Badge>
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Polling every {LIVE_TAIL_POLL_MS / 1000}s · showing last {LIVE_TAIL_LIMIT} entries ·{' '}
              {tailLastFetch ? `updated ${tailLastFetch.toLocaleTimeString()}` : 'waiting…'}
            </div>
          </div>
          <Button
            onClick={() => setTailPaused((p) => !p)}
            variant={tailPaused ? 'default' : 'outline'}
            size="sm"
            data-testid="tail-toggle-btn"
          >
            {tailPaused ? (
              <>
                <Play className="h-4 w-4 mr-2" /> Resume
              </>
            ) : (
              <>
                <Pause className="h-4 w-4 mr-2" /> Pause
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent className="pt-0 max-h-[420px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tail.map((l) => {
                const parts = (l.permission_name || '').split('.');
                const module = parts[0] || '—';
                const action = parts.slice(1).join('.') || '—';
                return (
                  <TableRow key={l.id} data-testid="tail-row">
                    <TableCell className="text-xs whitespace-nowrap">
                      {l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : '—'}
                    </TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={l.user_email || l.user_id}>
                      {l.user_email || l.user_id}
                    </TableCell>
                    <TableCell className="text-xs">
                      {l.role_level || '—'}
                      {l.rbac_role_name && (
                        <span className="text-slate-400"> → {l.rbac_role_name}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{module}</TableCell>
                    <TableCell className="font-mono text-xs">{action}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {l.scope || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {l.granted_by_rbac ? (
                        <Badge className="bg-green-600 text-white text-xs">allow</Badge>
                      ) : (
                        <Badge className="bg-red-600 text-white text-xs">deny</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!tail.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-10">
                    Waiting for permission checks…
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Simulate Enforcement (read-only dry-run) */}
      <Card data-testid="card-simulation">
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-purple-600" />
              Simulate Enforcement (Dry-Run)
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Read-only replay of the last N permission logs through the RBAC layer.
              No DB writes, no API calls, no side effects — safe to run live.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runSimulation}
              disabled={simLoading}
              size="sm"
              data-testid="sim-run-btn"
            >
              {simLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Running…
                </>
              ) : (
                <>
                  <FlaskConical className="h-4 w-4 mr-2" /> Run Simulation
                </>
              )}
            </Button>
            <Button
              onClick={exportSimulation}
              disabled={simLoading}
              variant="outline"
              size="sm"
              data-testid="sim-export-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={emailSimulation}
              disabled={emailSending || simLoading}
              variant="outline"
              size="sm"
              data-testid="sim-email-btn"
            >
              {emailSending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Email Report
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Recipient row + quota */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label htmlFor="sim-email-recipient" className="text-slate-500">
              Send to:
            </label>
            <input
              id="sim-email-recipient"
              type="email"
              placeholder={
                emailStatus?.configured_recipient ||
                'admin@example.com (or set RBAC_REPORT_EMAIL)'
              }
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              className="border rounded px-2 py-1 text-xs w-64"
              data-testid="sim-email-recipient"
            />
            {emailStatus && (
              <span
                className="text-slate-500"
                data-testid="sim-email-quota"
              >
                · {emailStatus.sent_last_24h}/{emailStatus.daily_limit} used in last 24h
                {emailStatus.daily_scheduled_enabled && (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                    daily auto-send ON
                  </span>
                )}
              </span>
            )}
          </div>
          {/* Filter controls */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Last N
              </label>
              <Select value={simLastN} onValueChange={setSimLastN}>
                <SelectTrigger className="h-8 text-xs" data-testid="sim-filter-n">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000 (max)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Role
              </label>
              <Select value={simRole} onValueChange={setSimRole}>
                <SelectTrigger className="h-8 text-xs" data-testid="sim-filter-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {(summary?.by_role_level || []).map((r) => (
                    <SelectItem key={r.role_level} value={r.role_level || 'unknown'}>
                      {r.role_level || '(unknown)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Module
              </label>
              <Select value={simModule} onValueChange={setSimModule}>
                <SelectTrigger className="h-8 text-xs" data-testid="sim-filter-module">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modules</SelectItem>
                  {EXPECTED_MODULES.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Action
              </label>
              <Select value={simAction} onValueChange={setSimAction}>
                <SelectTrigger className="h-8 text-xs" data-testid="sim-filter-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {['view', 'create', 'edit', 'delete', 'approve', 'schedule',
                    'update_status', 'assign', 'export', 'manage_users',
                    'manage_roles', 'manage_departments'].map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={simDeniedOnly}
                  onChange={(e) => setSimDeniedOnly(e.target.checked)}
                  data-testid="sim-filter-denied-only"
                />
                Would-be-denied only
              </label>
            </div>
          </div>

          {/* Decision + summary */}
          {simResult && (
            <>
              <div
                className={`border-l-4 p-4 rounded flex items-start gap-3 ${
                  simResult.safe_to_enforce
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                }`}
                data-testid="sim-decision-banner"
              >
                {simResult.safe_to_enforce ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600 mt-0.5" />
                )}
                <div>
                  <div
                    className={`font-semibold ${
                      simResult.safe_to_enforce ? 'text-green-900' : 'text-red-900'
                    }`}
                    data-testid="sim-decision-text"
                  >
                    {simResult.decision}
                  </div>
                  <div className="text-xs mt-0.5 text-slate-700">
                    Analyzed {simResult.analyzed_count} requests · would deny{' '}
                    <strong>{simResult.simulated_denies}</strong> ({simResult.deny_rate}%)
                    {simResult.high_impact_risk && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-semibold">
                        HIGH IMPACT RISK
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {simResult.deny_rate > 5 && (
                <div
                  className={`border-l-4 p-3 rounded flex items-start gap-3 ${
                    simResult.warning_level === 'critical'
                      ? 'border-red-600 bg-red-50'
                      : 'border-amber-500 bg-amber-50'
                  }`}
                  data-testid="sim-warning-banner"
                >
                  <AlertTriangle
                    className={`h-5 w-5 mt-0.5 ${
                      simResult.warning_level === 'critical'
                        ? 'text-red-600'
                        : 'text-amber-600'
                    }`}
                  />
                  <div className="text-sm">
                    <div className="font-semibold">
                      {simResult.warning_level === 'critical'
                        ? 'Critical: Deny rate above 10%'
                        : 'Warning: Deny rate above 5%'}
                    </div>
                    <div className="text-slate-700">
                      Investigate top denied actions and affected roles before flipping to{' '}
                      <code>enforce</code>.
                    </div>
                  </div>
                </div>
              )}

              {/* Summary KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Card data-testid="sim-kpi-analyzed">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase text-slate-500">Analyzed</div>
                    <div className="text-2xl font-bold">{simResult.analyzed_count}</div>
                  </CardContent>
                </Card>
                <Card data-testid="sim-kpi-denies">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase text-slate-500">Simulated denies</div>
                    <div className="text-2xl font-bold text-red-600">
                      {simResult.simulated_denies}
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="sim-kpi-rate">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase text-slate-500">Deny %</div>
                    <div className="text-2xl font-bold">{simResult.deny_rate}%</div>
                  </CardContent>
                </Card>
                <Card data-testid="sim-kpi-safe">
                  <CardContent className="p-4">
                    <div className="text-[10px] uppercase text-slate-500">Enforcement</div>
                    <div className="text-lg font-semibold">
                      {simResult.safe_to_enforce ? (
                        <span className="text-green-700">Safe</span>
                      ) : (
                        <span className="text-red-700">Review</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top breakdowns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card data-testid="sim-top-actions">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Top denied actions</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 max-h-48 overflow-auto">
                    {simResult.top_denied_actions?.length ? (
                      <ul className="text-xs space-y-1">
                        {simResult.top_denied_actions.map((a) => (
                          <li key={a.key} className="flex justify-between">
                            <span className="font-mono">{a.key}</span>
                            <span className="text-red-600 font-medium">{a.count}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-500">No denies — all clear.</p>
                    )}
                  </CardContent>
                </Card>
                <Card data-testid="sim-top-roles">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Top affected roles</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 max-h-48 overflow-auto">
                    {simResult.top_affected_roles?.length ? (
                      <ul className="text-xs space-y-1">
                        {simResult.top_affected_roles.map((r) => (
                          <li key={r.key} className="flex justify-between">
                            <span>{r.key}</span>
                            <span className="text-red-600 font-medium">{r.count}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-500">No affected roles.</p>
                    )}
                  </CardContent>
                </Card>
                <Card data-testid="sim-by-module">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Denies by module</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 max-h-48 overflow-auto">
                    {simResult.by_module_denies?.length ? (
                      <ul className="text-xs space-y-1">
                        {simResult.by_module_denies.map((m) => (
                          <li key={m.key} className="flex justify-between">
                            <span>{m.key}</span>
                            <span className="text-red-600 font-medium">{m.count}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-500">No module denies.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Per-request table */}
              <div className="max-h-[420px] overflow-auto border rounded">
                <Table data-testid="sim-requests-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Simulated</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simResult.requests.map((r) => (
                      <TableRow
                        key={r.log_id}
                        className={
                          r.simulated_result === 'deny' ? 'bg-red-50' : ''
                        }
                        data-testid={
                          r.simulated_result === 'deny'
                            ? 'sim-row-deny'
                            : 'sim-row-allow'
                        }
                      >
                        <TableCell className="text-xs max-w-[160px] truncate" title={r.user_email}>
                          {r.user_email || r.user_id}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.role_level || '—'}
                          {r.rbac_role_name && (
                            <span className="text-slate-400"> → {r.rbac_role_name}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.module}</TableCell>
                        <TableCell className="font-mono text-xs">{r.action}</TableCell>
                        <TableCell className="text-xs">{r.scope || '—'}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              r.actual_result === 'allow'
                                ? 'bg-green-600 text-white text-xs'
                                : 'bg-red-600 text-white text-xs'
                            }
                          >
                            {r.actual_result}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              r.simulated_result === 'allow'
                                ? 'bg-green-600 text-white text-xs'
                                : 'bg-red-600 text-white text-xs'
                            }
                          >
                            {r.simulated_result}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-red-700">
                          {r.deny_reason || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!simResult.requests.length && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">
                          No requests matched the filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {!simResult && !simLoading && (
            <div className="text-sm text-slate-500 text-center py-8">
              Click <strong>Run Simulation</strong> to dry-run the last N permission checks
              through <code>enforce</code> mode.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent logs (filterable) */}
      <Card data-testid="card-recent-logs">
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">Recent Permission Checks (filterable)</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={deniedOnly} onValueChange={setDeniedOnly}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-decision">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All decisions</SelectItem>
                <SelectItem value="granted">Granted only</SelectItem>
                <SelectItem value="denied">Denied only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={permissionFilter} onValueChange={setPermissionFilter}>
              <SelectTrigger className="w-52 h-8 text-xs" data-testid="filter-permission">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All permissions</SelectItem>
                {allPermissions.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="pt-0 max-h-[500px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Permission</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Decision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {l.timestamp ? new Date(l.timestamp).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-xs">{l.user_email || l.user_id}</TableCell>
                  <TableCell className="text-xs">
                    {l.role_level || '—'}
                    {l.rbac_role_name && (
                      <span className="text-slate-400"> → {l.rbac_role_name}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{l.permission_name}</TableCell>
                  <TableCell
                    className="font-mono text-xs max-w-[200px] truncate"
                    title={l.endpoint}
                  >
                    {l.endpoint || '—'}
                  </TableCell>
                  <TableCell className="text-xs">{l.method || '—'}</TableCell>
                  <TableCell className="text-xs">{l.scope || '—'}</TableCell>
                  <TableCell>
                    {l.granted_by_rbac ? (
                      <Badge
                        className="bg-green-600 text-white text-xs"
                        data-testid="decision-granted"
                      >
                        granted
                      </Badge>
                    ) : (
                      <Badge
                        className="bg-red-600 text-white text-xs"
                        data-testid="decision-denied"
                      >
                        denied
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!logs.length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-10">
                    No logs matching filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Footer notes */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4 text-xs text-slate-600">
          <p className="font-medium mb-1">How to read this dashboard</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>
              <strong>Deny Rate</strong> = how many checks the new RBAC system would have blocked.
              While mode is <code>off</code> or <code>log_only</code>, denies are purely
              informational.
            </li>
            <li>
              <strong>Potential misconfiguration</strong> banners flag roles whose RBAC mapping
              disagrees with real usage — investigate before flipping to <code>enforce</code>.
            </li>
            <li>
              <strong>Missing coverage</strong> banner means a module has zero logs yet — either
              no traffic hit it, or the logging hook is missing.
            </li>
            <li>
              To toggle enforcement: set <code>RBAC_ENFORCEMENT_MODE=enforce</code> in{' '}
              <code>/app/backend/.env</code> and restart backend. Default validation value is{' '}
              <code>log_only</code>.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default RBACDebug;
